import React, { useMemo } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import {useVacationReportSearch} from '../../search';
import VacationDiagramRedesigned, {GroupedDiagramItem, VacationDiagramDay} from './index';
import {Year} from '../../../../features/settings/workcalendar/types';
import {VacationDateRangeFilter, VacationReportItem, VacationState, isRenderableVacationState} from '../../../../features/vacation/types';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';

// ---- helpers copied from current impl ----
function getVacationDiagramDays(
    years: Year[],
    filter: VacationDateRangeFilter
): VacationDiagramDay[] {
    const vacationDiagramDays: VacationDiagramDay[] = [];
    const end = moment(filter.endDate, 'YYYY-MM-DD');
    const start = moment(filter.startDate, 'YYYY-MM-DD');

    years.forEach((year) => {
        year.months.forEach((month) => {
            month.days.forEach((day) => {
                const current = moment([year.year, month.month - 1, day.day]);
                if (current >= start && current <= end) {
                    vacationDiagramDays.push({
                        numberDayOfMonth: day.day,
                        date: current.clone(),
                        isDayOff: day.isHoliday,
                        isShort: day.isShort,
                    });
                }
            });
        });
    });
    return vacationDiagramDays;
}

function groupItems(items: VacationReportItem[]): GroupedDiagramItem[] {
    // group by employee and collect durations
    const byEmp = _.groupBy(items, (x) => x.employeeId);
    return Object.values(byEmp).map((group) => {
        const first = group[0]!;
        return {
            employeeId: first.employeeId,
            employeeName: first.employeeName,
            durations: group
                .filter((x) => x.dateTo && x.dateFrom && isRenderableVacationState(x.state))
                .map((it) => ({
                    id: it.id,
                    employeeId: it.employeeId,
                    dateFrom: moment(it.dateFrom),
                    dateTo: moment(it.dateTo),
                    state: it.state,
                    approvers: it.approvers,
                })),
        };
    });
}

function sortAndFilterEmployees(
    grouped: GroupedDiagramItem[],
    search: string
): GroupedDiagramItem[] {
    const ordered = _.orderBy(
        grouped,
        [
            (x) => x.employeeName.toLowerCase(),
            // put Undefined state last if needed; we’ll check if any duration is Undefined
            (x) => x.durations.some((d) => d.state === VacationState.Undefined),
        ],
        ['asc', 'asc']
    );

    const q = (search ?? '').trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((x) => x.employeeName.toLowerCase().includes(q));
}

// ---- Adapter component ----
export default function VacationDiagramAdapter({
    vacationReportItems,
    years,
    dateFilter,
}: {
    vacationReportItems: VacationReportItem[];
    years: Year[];
    dateFilter: VacationDateRangeFilter;
}) {
    const { value: search } = useVacationReportSearch();
    const navigate = useNavigate();

    // derive days for header/grid
    const days = useMemo(
        () => (years?.length ? getVacationDiagramDays(years, dateFilter) : []),
        [years, dateFilter]
    );

    // group items per employee (like your current reduce)
    const grouped = useMemo(
        () => sortAndFilterEmployees(groupItems(vacationReportItems), search),
        [vacationReportItems, search]
    );

    if (!days.length || !grouped.length) {
        return null; // or your empty state
    }

    // redesigned component expects { employees, days }
    return (
        <VacationDiagramRedesigned
            employees={grouped}
            days={days}
            onCreateRange={(employee, rangeStart, rangeEnd) => {
                navigate(NAVIGATION_ROUTES.CREATE_VACATION, {
                    state: {
                        employeeId: employee.employeeId,
                        employeeName: employee.employeeName,
                        dateFrom: rangeStart.format('YYYY-MM-DD'),
                        dateTo: rangeEnd.format('YYYY-MM-DD'),
                    }
                });
            }}
        />
    );
}
